'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { trackEvent } from '@/lib/analytics';
import AirportAutocomplete from '@/components/common/AirportAutocomplete';

type SearchTab = 'flights' | 'hotels' | 'holidays';

const UnifiedSearchWidget = () => {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<SearchTab>('flights');
  
  // Flight search state
  const [flightFrom, setFlightFrom] = useState('');
  const [flightTo, setFlightTo] = useState('');
  const [flightDate, setFlightDate] = useState('');
  const [flightReturn, setFlightReturn] = useState('');
  const [flightPassengers, setFlightPassengers] = useState(1);
  const [tripType, setTripType] = useState<'round-trip' | 'one-way'>('round-trip');
  
  // Hotel search state
  const [hotelDestination, setHotelDestination] = useState('');
  const [hotelCheckIn, setHotelCheckIn] = useState('');
  const [hotelCheckOut, setHotelCheckOut] = useState('');
  const [hotelGuests, setHotelGuests] = useState(2);
  const [hotelRooms, setHotelRooms] = useState(1);
  
  // Holiday search state
  const [holidayDestination, setHolidayDestination] = useState('');
  const [holidayDuration, setHolidayDuration] = useState('7');
  const [holidayBudget, setHolidayBudget] = useState('medium');
  const [holidayTravelers, setHolidayTravelers] = useState(2);

  const handleFlightSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!flightFrom.trim() || !flightTo.trim()) return;
    trackEvent('homepage_flight_search', { from: flightFrom, to: flightTo, passengers: flightPassengers });
    const params = new URLSearchParams({
      from: flightFrom,
      to: flightTo,
      date: flightDate,
      return: flightReturn,
      passengers: String(flightPassengers),
      tripType,
    });
    router.push(`/flight-search?${params.toString()}`);
  };

  const handleHotelSearch = (e: React.FormEvent) => {
    e.preventDefault();
    trackEvent('homepage_hotel_search', { destination: hotelDestination, guests: hotelGuests, rooms: hotelRooms });
    router.push(`/hotels?destination=${encodeURIComponent(hotelDestination)}&checkin=${hotelCheckIn}&checkout=${hotelCheckOut}&guests=${hotelGuests}&rooms=${hotelRooms}`);
  };

  const handleHolidaySearch = (e: React.FormEvent) => {
    e.preventDefault();
    trackEvent('homepage_holiday_search', { destination: holidayDestination, duration: holidayDuration });
    router.push(`/holidays?destination=${encodeURIComponent(holidayDestination)}&duration=${holidayDuration}&budget=${holidayBudget}&travelers=${holidayTravelers}`);
  };

  return (
    <div className="max-w-5xl mx-auto animate-fade-in transform-3d relative z-20" style={{ animationDelay: '0.3s' }}>
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-3d shine-effect border-4 border-accent/30 overflow-hidden">
        {/* Tab Headers */}
        <div className="flex border-b-2 border-border/30">
          {(['flights', 'hotels', 'holidays'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-6 py-4 font-heading font-black text-base transition-all duration-200 flex items-center justify-center gap-2 ${
                activeTab === tab
                  ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg'
                  : 'bg-white text-foreground hover:bg-surface'
              }`}
            >
              <Icon
                name={tab === 'flights' ? 'PaperAirplaneIcon' : tab === 'hotels' ? 'BuildingOffice2Icon' : 'GlobeAltIcon'}
                size={20}
              />
              <span className="capitalize">{tab}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Flights Tab */}
          {activeTab === 'flights' && (
            <form onSubmit={handleFlightSearch} className="space-y-4">
              {/* Trip Type */}
              <div className="flex gap-3">
                <button type="button" onClick={() => setTripType('round-trip')}
                  className={`px-4 py-1.5 rounded-full text-sm font-black border-2 transition-all ${
                    tripType === 'round-trip' ? 'bg-primary text-white border-primary' : 'border-border text-foreground hover:border-primary'
                  }`}>
                  Round Trip
                </button>
                <button type="button" onClick={() => setTripType('one-way')}
                  className={`px-4 py-1.5 rounded-full text-sm font-black border-2 transition-all ${
                    tripType === 'one-way' ? 'bg-primary text-white border-primary' : 'border-border text-foreground hover:border-primary'
                  }`}>
                  One Way
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AirportAutocomplete
                  value={flightFrom}
                  onChange={setFlightFrom}
                  placeholder="From (City or Airport)"
                  label="From"
                  required
                />
                <AirportAutocomplete
                  value={flightTo}
                  onChange={setFlightTo}
                  placeholder="To (City or Airport)"
                  label="To"
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-black text-foreground mb-1">Departure Date</label>
                  <div className="relative">
                    <Icon name="CalendarIcon" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input type="date" value={flightDate} onChange={e => setFlightDate(e.target.value)}
                      className="w-full pl-10 pr-3 py-3.5 border-2 border-border rounded-xl focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold text-foreground"
                      required />
                  </div>
                </div>
                {tripType === 'round-trip' && (
                  <div>
                    <label className="block text-xs font-black text-foreground mb-1">Return Date</label>
                    <div className="relative">
                      <Icon name="CalendarIcon" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      <input type="date" value={flightReturn} onChange={e => setFlightReturn(e.target.value)}
                        className="w-full pl-10 pr-3 py-3.5 border-2 border-border rounded-xl focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold text-foreground" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-black text-foreground mb-1">Passengers</label>
                  <div className="relative">
                    <Icon name="UserGroupIcon" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <select value={flightPassengers} onChange={e => setFlightPassengers(parseInt(e.target.value))}
                      className="w-full pl-10 pr-3 py-3.5 border-2 border-border rounded-xl focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold text-foreground appearance-none">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                        <option key={num} value={num}>{num} Passenger{num > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <button type="submit"
                className="w-full px-8 py-4 gradient-gold-shiny text-primary font-heading font-black text-lg rounded-xl shadow-3d transition-all duration-300 hover:scale-105 active:scale-95 shine-effect border-2 border-accent flex items-center justify-center gap-2">
                <Icon name="MagnifyingGlassIcon" size={22} />
                Search Flights
              </button>
            </form>
          )}

          {/* Hotels Tab */}
          {activeTab === 'hotels' && (
            <form onSubmit={handleHotelSearch} className="space-y-4">
              <div className="relative">
                <Icon name="MapPinIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input type="text" value={hotelDestination} onChange={e => setHotelDestination(e.target.value)}
                  placeholder="Where are you going?"
                  className="w-full pl-12 pr-4 py-4 border-2 border-border rounded-xl focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold text-foreground"
                  required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <Icon name="CalendarIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input type="date" value={hotelCheckIn} onChange={e => setHotelCheckIn(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 border-2 border-border rounded-xl focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold text-foreground" required />
                </div>
                <div className="relative">
                  <Icon name="CalendarIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input type="date" value={hotelCheckOut} onChange={e => setHotelCheckOut(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 border-2 border-border rounded-xl focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold text-foreground" required />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <Icon name="UserGroupIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <select value={hotelGuests} onChange={e => setHotelGuests(parseInt(e.target.value))}
                    className="w-full pl-12 pr-4 py-4 border-2 border-border rounded-xl focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold text-foreground appearance-none">
                    {[1, 2, 3, 4, 5, 6].map(num => (
                      <option key={num} value={num}>{num} Guest{num > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <Icon name="HomeIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <select value={hotelRooms} onChange={e => setHotelRooms(parseInt(e.target.value))}
                    className="w-full pl-12 pr-4 py-4 border-2 border-border rounded-xl focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold text-foreground appearance-none">
                    {[1, 2, 3, 4, 5].map(num => (
                      <option key={num} value={num}>{num} Room{num > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button type="submit"
                className="w-full px-8 py-4 gradient-gold-shiny text-primary font-heading font-black text-lg rounded-xl shadow-3d transition-all duration-300 hover:scale-105 active:scale-95 shine-effect border-2 border-accent flex items-center justify-center gap-2">
                <Icon name="MagnifyingGlassIcon" size={22} />
                Search Hotels
              </button>
            </form>
          )}

          {/* Holidays Tab */}
          {activeTab === 'holidays' && (
            <form onSubmit={handleHolidaySearch} className="space-y-4">
              <div className="relative">
                <Icon name="GlobeAltIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input type="text" value={holidayDestination} onChange={e => setHolidayDestination(e.target.value)}
                  placeholder="Dream destination or region"
                  className="w-full pl-12 pr-4 py-4 border-2 border-border rounded-xl focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold text-foreground"
                  required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Icon name="ClockIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <select value={holidayDuration} onChange={e => setHolidayDuration(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 border-2 border-border rounded-xl focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold text-foreground appearance-none">
                    {['3', '5', '7', '10', '14', '21'].map(d => (
                      <option key={d} value={d}>{d} nights</option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <Icon name="CurrencyPoundIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <select value={holidayBudget} onChange={e => setHolidayBudget(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 border-2 border-border rounded-xl focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold text-foreground appearance-none">
                    <option value="budget">Budget</option>
                    <option value="medium">Mid-range</option>
                    <option value="luxury">Luxury</option>
                  </select>
                </div>
                <div className="relative">
                  <Icon name="UserGroupIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <select value={holidayTravelers} onChange={e => setHolidayTravelers(parseInt(e.target.value))}
                    className="w-full pl-12 pr-4 py-4 border-2 border-border rounded-xl focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold text-foreground appearance-none">
                    {[1, 2, 3, 4, 5, 6].map(num => (
                      <option key={num} value={num}>{num} Traveller{num > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button type="submit"
                className="w-full px-8 py-4 gradient-gold-shiny text-primary font-heading font-black text-lg rounded-xl shadow-3d transition-all duration-300 hover:scale-105 active:scale-95 shine-effect border-2 border-accent flex items-center justify-center gap-2">
                <Icon name="MagnifyingGlassIcon" size={22} />
                Search Holidays
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnifiedSearchWidget;